'use client';

import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff, Mail, Lock, Phone, User, Calendar, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import AlertDialog from '@/components/AlertDialog';
import { useRouter, useSearchParams } from 'next/navigation';
import AuthPageGuard from '@/components/AuthPageGuard';
import Dropdown from '@/components/Dropdown';
import SearchableDropdown, { SearchableOption } from '@/components/SearchableDropdown';
import { useDropdownLoading, useDropdownOptions } from '@/contexts/DropdownContext';
import { useDropdowns } from '@/contexts/EnhancedDropdownContext';
import { useDependentDropdown } from '@/hooks/useDependentDropdown';
import { authService } from '@/services/authService';
import { getErrorMessage } from '@/types/error';
import { getEffectiveCampaignParams } from '@/utils/campaignParams';

// Types
interface RegisterFormValues {
  gender: string;
  maritalStatusId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  password: string;
  religionId: string;
  casteId: string;
  motherTongue: string;
  educationId: string;
  occupationId: string;
  country: string;
  state: string;
  city: string;
  dateOfBirth: string;
  termsAccepted: boolean;
}

// Static gender options (not in API)
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

// Validation schema
const registerSchema = Yup.object().shape({
  gender: Yup.string()
    .oneOf(['male', 'female'], 'Please select a valid gender')
    .required('Gender is required'),
  maritalStatusId: Yup.string()
    .min(1, 'Please select a valid marital status')
    .required('Marital status is required'),
  firstName: Yup.string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(1, 'Last name must be at least 1 character')
    .max(50, 'Last name must be less than 50 characters')
    .required('Last name is required'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  phoneCountryCode: Yup.string()
    .required('Phone country code is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit phone number')
    .required('Phone number is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters long')
    .required('Password is required'),
  religionId: Yup.string()
    .min(1, 'Please select a valid religion')
    .required('Religion is required'),
  casteId: Yup.string()
    .min(1, 'Please select a valid caste')
    .required('Caste is required')
    .test('caste-required', 'Please select a caste for the chosen religion', function(value) {
      const { religionId } = this.parent;
      // If religion is selected, caste must also be selected
      if (religionId && !value) {
        return false;
      }
      return true;
    }),
  motherTongue: Yup.string()
    .min(1, 'Please select a valid mother tongue')
    .required('Mother tongue is required'),
  educationId: Yup.string()
    .min(1, 'Please select a valid education')
    .required('Education is required'),
  occupationId: Yup.string()
    .min(1, 'Please select a valid occupation')
    .required('Occupation is required'),
  country: Yup.string()
    .min(1, 'Please select a valid country')
    .required('Country is required'),
  state: Yup.string()
    .min(1, 'Please select a valid state')
    .required('State is required'),
  city: Yup.string()
    .min(1, 'Please select a valid city')
    .required('City is required'),
  dateOfBirth: Yup.date()
    .max(new Date(), 'Date of birth cannot be in the future')
    .required('Date of birth is required')
    .test('age-requirement', 'Age requirement not met', function(value) {
      const { gender } = this.parent;
      if (!value || !gender) return true;
      
      const today = new Date();
      const birthDate = new Date(value);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
        ? age - 1 
        : age;
      
      if (gender === 'female' && actualAge < 18) {
        return this.createError({ message: 'Female users must be at least 18 years old' });
      }
      
      if (gender === 'male' && actualAge < 21) {
        return this.createError({ message: 'Male users must be at least 21 years old' });
      }
      
      return true;
    }),
  termsAccepted: Yup.boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
});

// FAQs from Mobile App FAQ (register / app usage) – answers as React nodes for formatting
const REGISTER_FAQS: { question: string; answer: React.ReactNode }[] = [
  {
    question: 'How to do free registration?',
    answer: (
      <>
        <p className="mb-3">For free registration:</p>
        <ol className="list-decimal list-inside space-y-2 ml-1 mb-3">
          <li>Visit <strong>www.MarathiVadhuVarSuchak.com</strong> and click <strong>Register Now in 2 minutes</strong>.</li>
          <li>Provide mandatory information: Gender, Marital Status, First Name, Last Name, Email Address, Phone Number, Password, Religion, Caste, Mother Tongue, and Date of Birth.</li>
          <li>Select Terms &amp; Conditions and Privacy Policy.</li>
          <li>Enter the <strong>OTP</strong> received on SMS to verify your mobile.</li>
          <li>Click <strong>Create Account</strong>.</li>
        </ol>
        <p className="mb-2">Your profile will be created and your <strong>MVV ID</strong> will be generated. You will land on the Dashboard where you can add:</p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>Profile photo</li>
          <li>Professional, location, physical, religious &amp; family details</li>
          <li>Partner preferences</li>
        </ul>
      </>
    )
  },
  {
    question: 'What is the status profile pending for approval on my dashboard?',
    answer: (
      <>
        <p className="mb-2">All profiles are validated before publishing on the Marathi Vadhu Var Suchak site and mobile app.</p>
        <ul className="list-disc list-inside space-y-1 ml-1 mb-2">
          <li>Express validation may take <strong>1 to 2 hours</strong>.</li>
          <li>Our support team validates each profile on specific criteria before publishing.</li>
        </ul>
        <p>Once your profile is approved, a notification will be sent to your verified mobile number and email.</p>
      </>
    )
  },
  {
    question: 'On what criteria was my profile validated?',
    answer: (
      <>
        <p className="mb-2">Our team reads every submission carefully. We reject profiles that:</p>
        <ul className="list-disc list-inside space-y-1 ml-1 mb-2">
          <li>Are from underage persons</li>
          <li>Contain abusive content or language</li>
          <li>Have invalid data or are added for fun / friendship / not related to marriage</li>
        </ul>
        <p>We maintain the highest quality in the profile database to ensure only members serious about marriage are served.</p>
      </>
    )
  },
  {
    question: 'What should I enter in the profile description field?',
    answer: (
      <>
        <p className="mb-2">Your profile description is where you reveal yourself. Include:</p>
        <ul className="list-disc list-inside space-y-1 ml-1 mb-3">
          <li>Personality, likes and dislikes</li>
          <li>Educational and family background</li>
          <li>Career and personal interests</li>
          <li>Expectations about your partner</li>
        </ul>
        <p className="mb-2">Give a clear self-description and a complete picture of what you expect. A good description gets more responses.</p>
        <p>You can also use our sample profile descriptions as a reference.</p>
      </>
    )
  },
  {
    question: 'What is MVV ID?',
    answer: (
      <p>An <strong>MVV ID</strong> is a unique combination of letters and a six-digit number (e.g., MVV101285). It is assigned to you when you register with MarathiVadhuVarSuchak.com and identifies you uniquely on the platform.</p>
    )
  },
  {
    question: 'I forgot my MVV ID/Password. What should I do?',
    answer: (
      <>
        <p className="mb-2">You can log in with <strong>OTP</strong> or reset your password:</p>
        <ol className="list-decimal list-inside space-y-1 ml-1">
          <li>Enter your registered mobile number to receive an OTP.</li>
          <li>Enter the OTP received via SMS to access your account or set a new password.</li>
        </ol>
      </>
    )
  },
  {
    question: 'What is partner preference?',
    answer: (
      <p><strong>Partner Preference</strong> helps you define your ideal spouse and set suitability criteria. You will receive email and mobile notifications daily about new profiles matching your criteria.</p>
    )
  },
  {
    question: "What are the DO's and DON'Ts while creating a matrimonial profile?",
    answer: (
      <>
        <p className="font-semibold text-gray-800 mb-2">Do&apos;s</p>
        <ul className="list-disc list-inside space-y-1 ml-1 mb-3">
          <li>Give brief information about yourself: personality, educational and professional background, family, career plans, expectations from your soul mate.</li>
          <li>Add a latest photograph for more responses — profiles with photos get better proposals.</li>
        </ul>
        <p className="font-semibold text-gray-800 mb-2">Don&apos;ts</p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>Do not provide an overly lengthy description.</li>
          <li>Do not display contact details or social media IDs in the description.</li>
          <li>Avoid vulgar or racist content.</li>
          <li>Do not write in filmy language; it may showcase immaturity.</li>
        </ul>
      </>
    )
  },
  {
    question: 'What is Quick Search?',
    answer: (
      <p><strong>Quick Search</strong> lets you search for profiles with a photo in just one click.</p>
    )
  },
  {
    question: 'Is online payment secure?',
    answer: (
      <>
        <p className="mb-2">Yes. It is secure to make online payments on Marathi Vadhu Var Suchak.</p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li>Our payment gateway uses <strong>SSL (Secure Socket Layer)</strong> technology.</li>
          <li>Your card details are kept secure and protected from unauthorized access.</li>
        </ul>
      </>
    )
  },
  {
    question: 'What is Block Profile feature?',
    answer: (
      <>
        <p className="mb-2">To block a profile:</p>
        <ol className="list-decimal list-inside space-y-1 ml-1 mb-2">
          <li>Log in to your profile.</li>
          <li>Select or search for the member&apos;s ID.</li>
          <li>Use the option (top right <strong>3 dots</strong>) to block the profile.</li>
        </ol>
        <p>That profile will be added to your Block List. Blocked members cannot contact you or express interest, and you cannot contact them.</p>
      </>
    )
  },
  {
    question: 'How do I contact customer support?',
    answer: (
      <>
        <p className="mb-2">You can get help via chat or call:</p>
        <ul className="list-none space-y-1 ml-0">
          <li><strong>Call / WhatsApp:</strong>{' '}
            <a href={`tel:${process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER || '919326013123'}`} className="text-primary-600 hover:text-primary-700 hover:underline">{process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER || '+91-9326013123'}</a>
          </li>
          <li><strong>Email:</strong>{' '}
            <a href="mailto:contact@marathivadhuvarsuchak.com" className="text-primary-600 hover:text-primary-700 hover:underline">contact@marathivadhuvarsuchak.com</a>
          </li>
          <li><strong>Business hours:</strong> Monday – Saturday, 10:30 AM - 6:00 PM</li>
          <li><strong>Sunday:</strong> Closed</li>
        </ul>
      </>
    )
  },
  {
    question: 'What is Profile Short-list?',
    answer: (
      <p>You can shortlist profiles you like. All shortlisted profiles are grouped in one place for easy access.</p>
    )
  },
  {
    question: 'What packages are available for your paid services?',
    answer: (
      <>
        <p className="mb-2">Our paid plans are affordable:</p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li><strong>1 Year Plan</strong> — ₹2,000</li>
          <li><strong>3 Year Plan</strong> — ₹4,500</li>
        </ul>
      </>
    )
  }
];

export default function Register() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [religionId, setReligionId] = useState('');
  const [alertDialog, setAlertDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error' as 'success' | 'warning' | 'info' | 'error'
  });
  const router = useRouter();
  const { isLoading: dropdownLoading, error: dropdownError } = useDropdownLoading();
  const {
    countries,
    states,
    cities,
    fetchStatesByCountry,
    fetchCitiesByState,
    getOptionsForKey,
  } = useDropdowns();
  const searchParams = useSearchParams();
  
  // Use dependent dropdown hook for caste
  const { options: casteOptions, isLoading: casteLoading, error: casteError } = useDependentDropdown({
    parentKey: 'religion',
    parentValue: religionId,
    dependentKey: 'caste'
  });

  // Get dropdown options
  const religionOptions = useDropdownOptions('RELIGION');
  const maritalStatusOptions = useDropdownOptions('MARITAL_STATUS');
  const motherTongueOptions = useDropdownOptions('MOTHER_TONGUE');

  // Preload default location (India -> Maharashtra) for dependent dropdowns
  useEffect(() => {
    // 101 = India, 1677 = Maharashtra
    fetchStatesByCountry('101');
    fetchCitiesByState('1677');
  }, [fetchStatesByCountry, fetchCitiesByState]);

  const handleRegister = async (values: RegisterFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setIsLoading(true);
    try {
      // Effective campaign params: URL (current visit) first, then stored (2-week TTL from landing)
      const get = (key: string) => searchParams?.get(key) ?? null;
      const campaign = getEffectiveCampaignParams(get);
      const { utmSource, utmMedium, utmCampaign, affiliateCode } = campaign;

      const response = await authService.register({
        gender: values.gender,
        maritalStatusId: values.maritalStatusId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneCountryCode: values.phoneCountryCode,
        phone: values.phone,
        password: values.password,
        religionId: values.religionId,
        casteId: values.casteId,
        motherTongue: values.motherTongue,
        educationId: values.educationId,
        occupationId: values.occupationId,
        country: parseInt(values.country, 10),
        state: parseInt(values.state, 10),
        city: parseInt(values.city, 10),
        dateOfBirth: values.dateOfBirth,
        termsAccepted: values.termsAccepted,
        registrationSource: 'web',
        ...(utmSource && { utmSource }),
        ...(utmMedium && { utmMedium }),
        ...(utmCampaign && { utmCampaign }),
        ...(affiliateCode && { affiliateCode }),
      });
      
      // Check if OTP verification is required
      if (response.data.requiresOTPVerification) {
        // Redirect to verify-profile page with user data
        const searchParams = new URLSearchParams({
          email: response.data.email,
          phone: response.data.phone,
          countryCode: values.phoneCountryCode,
          userId: response.data.userId,
          from: 'register' // Add source parameter to identify registration flow
        });
        
        router.push(`/verify-profile?${searchParams.toString()}`);
      } else {
        // If no OTP required, redirect to dashboard
        router.push('/dashboard');
      }
      
    } catch (error: unknown) {
      // Handle specific error cases
      const errorMessage = getErrorMessage(error, 'Registration failed. Please try again.');
      setAlertDialog({
        isOpen: true,
        title: 'Registration Failed',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <AuthPageGuard>
      <div className="bg-white">
        {/* Header */}
        <Header variant="fixed" />

      {/* Content Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">मोफत नोंदणी</h1>
              <p className="text-lg text-gray-600">फोटोसहित संपूर्ण माहिती भरा, तुमच्या प्रोफाईलची विश्वासार्हता वाढेल!</p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 max-[599px]:p-6 min-w-0 overflow-x-hidden">
              {/* Dropdown Loading State */}
              {dropdownLoading && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">Loading form options...</p>
                </div>
              )}

              {/* Dropdown Error State */}
              {dropdownError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">Error loading form options: {dropdownError}</p>
                </div>
              )}

              <Formik<RegisterFormValues>
                initialValues={{
                  gender: '',
                  maritalStatusId: '',
                  firstName: '',
                  lastName: '',
                  email: '',
                  phoneCountryCode: '+91',
                  phone: '',
                  password: '',
                  religionId: '',
                  casteId: '',
                  motherTongue: '1', // Default: Marathi
                  educationId: '',
                  occupationId: '',
                  // Default location: India (101), Maharashtra (1677)
                  country: '101',
                  state: '1677',
                  city: '',
                  dateOfBirth: '',
                  termsAccepted: false,
                }}
                validationSchema={registerSchema}
                onSubmit={(values, actions) => {
                  return handleRegister(values, actions);
                }}
              >
        {({ isSubmitting, setFieldValue, values }) => {
                  
                  return (
                    <Form className="space-y-6">
                    {/* Gender and Marital Status Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                          Gender *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="gender"
                            name="gender"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                          >
                            <option value="">Select Gender</option>
                            {genderOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="gender" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      <div>
                        <label htmlFor="maritalStatusId" className="block text-sm font-medium text-gray-700 mb-2">
                          Marital Status *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="maritalStatusId"
                            name="maritalStatusId"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                          >
                            <option value="">Select Marital Status</option>
                            {maritalStatusOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="maritalStatusId" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* First Name and Last Name Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <div className="relative">
                          <Field
                            type="text"
                            id="firstName"
                            name="firstName"
                            placeholder="Enter first name"
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          />
                          <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <ErrorMessage name="firstName" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <div className="relative">
                          <Field
                            type="text"
                            id="lastName"
                            name="lastName"
                            placeholder="Enter last name"
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          />
                          <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <ErrorMessage name="lastName" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Field
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter your email"
                          className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        />
                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      </div>
                      <ErrorMessage name="email" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    {/* Phone Number Row */}
                    <div className="grid grid-cols-3 gap-4 max-[599px]:grid-cols-1 max-[599px]:gap-4">
                      <Dropdown
                        dropdownKey="COUNTRY_DIAL_CODES"
                        name="phoneCountryCode"
                        label="Country Code"
                        placeholder="Select Code"
                        required
                      />

                      <div className="col-span-2 max-[599px]:col-span-1">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <Field
                            type="tel"
                            id="phone"
                            name="phone"
                            placeholder="Enter 10-digit phone number"
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                          />
                          <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        </div>
                        <ErrorMessage name="phone" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <Field
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          placeholder="Enter your password"
                          className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                        />
                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <ErrorMessage name="password" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    {/* Religion and Caste Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="religionId" className="block text-sm font-medium text-gray-700 mb-2">
                          Religion *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="religionId"
                            name="religionId"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const value = e.target.value;
                              setReligionId(value);
                              // Update Formik field value
                              setFieldValue('religionId', value);
                              // Reset caste when religion changes
                              setFieldValue('casteId', '');
                            }}
                          >
                            <option value="">Select Religion</option>
                            {religionOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="religionId" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      <div>
                        <SearchableDropdown
                          options={casteOptions.map(option => ({
                            value: option.value,
                            label: option.label,
                            id: option.value
                          }))}
                          value={values.casteId}
                          onChange={(value) => setFieldValue('casteId', value)}
                          placeholder={casteLoading ? 'Loading castes...' : !religionId ? 'Select Religion first' : 'Search and select caste...'}
                          isDisabled={!religionId || casteLoading}
                          isLoading={casteLoading}
                          label="Caste"
                          required={true}
                          error={casteError || ''}
                          name="casteId"
                        />
                        <ErrorMessage name="casteId" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Mother Tongue */}
                    <div>
                      <label htmlFor="motherTongue" className="block text-sm font-medium text-gray-700 mb-2">
                        Mother Tongue *
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          id="motherTongue"
                          name="motherTongue"
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                        >
                          <option value="">Select Mother Tongue</option>
                          {motherTongueOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                      <ErrorMessage name="motherTongue" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    {/* Location: Country, State, City */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Country */}
                      <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                          Country *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="country"
                            name="country"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const value = e.target.value;
                              setFieldValue('country', value);
                              // Reset dependent fields
                              setFieldValue('state', '');
                              setFieldValue('city', '');
                              if (value) {
                                fetchStatesByCountry(value);
                              }
                            }}
                          >
                            <option value="">Select Country</option>
                            {countries &&
                              Object.entries(countries).map(([id, name]) => (
                                <option key={id} value={id}>
                                  {name as string}
                                </option>
                              ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="country" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      {/* State */}
                      <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                          State *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="state"
                            name="state"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={!values.country}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const value = e.target.value;
                              setFieldValue('state', value);
                              // Reset dependent city when state changes
                              setFieldValue('city', '');
                              if (value) {
                                fetchCitiesByState(value);
                              }
                            }}
                          >
                            <option value="">Select State</option>
                            {values.country &&
                              states?.[values.country] &&
                              Object.entries(states[values.country]).map(([id, name]) => (
                                <option key={id} value={id}>
                                  {name as string}
                                </option>
                              ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="state" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      {/* City */}
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                          City *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="city"
                            name="city"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={!values.state}
                          >
                            <option value="">Select City</option>
                            {values.state &&
                              cities?.[values.state] &&
                              Object.entries(cities[values.state]).map(([id, name]) => (
                                <option key={id} value={id}>
                                  {name as string}
                                </option>
                              ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="city" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Education and Occupation Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="educationId" className="block text-sm font-medium text-gray-700 mb-2">
                          Education *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="educationId"
                            name="educationId"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                          >
                            <option value="">Select Education</option>
                            {getOptionsForKey('EDUCATION_OPTIONS').map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="educationId" component="div" className="mt-1 text-sm text-red-600" />
                      </div>

                      <div>
                        <label htmlFor="occupationId" className="block text-sm font-medium text-gray-700 mb-2">
                          Occupation *
                        </label>
                        <div className="relative">
                          <Field
                            as="select"
                            id="occupationId"
                            name="occupationId"
                            className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 appearance-none bg-white cursor-pointer"
                          >
                            <option value="">Select Occupation</option>
                            {getOptionsForKey('OCCUPATION_OPTIONS').map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <ErrorMessage name="occupationId" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Date of Birth */}
                    <div className="w-full min-w-0">
                      <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <div className="relative w-full min-w-0">
                        <Field
                          type="date"
                          id="dateOfBirth"
                          name="dateOfBirth"
                          className="form-date-input w-full block min-w-0 max-w-full px-4 py-3 pl-12 pr-5 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 box-border cursor-pointer"
                        />
                        <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Age requirement: Female users must be at least 18 years old, Male users must be at least 21 years old
                      </p>
                      <ErrorMessage name="dateOfBirth" component="div" className="mt-1 text-sm text-red-600" />
                    </div>

                    {/* Terms and Conditions */}
                    <div className="flex items-start space-x-3">
                      <div className="flex items-center h-5">
                        <Field
                          type="checkbox"
                          id="termsAccepted"
                          name="termsAccepted"
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </div>
                      <div className="text-sm">
                        <label htmlFor="termsAccepted" className="text-gray-700">
                          I accept the{' '}
                          <Link href="/terms-and-conditions" className="text-primary-600 hover:text-primary-700 font-medium">
                            Terms & Conditions
                          </Link>
                          {' '}and{' '}
                          <Link href="/privacy-policy" className="text-primary-600 hover:text-primary-700 font-medium">
                            Privacy Policy
                          </Link>{' '}
                          *
                        </label>
                        <ErrorMessage name="termsAccepted" component="div" className="mt-1 text-sm text-red-600" />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading}
                      className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </button>

                    {/* Login Link */}
                    <div className="text-center">
                      <p className="text-gray-600">
                        Already have an account?{' '}
                        <Link href="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                          Sign In
                        </Link>
                      </p>
                    </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {REGISTER_FAQS.map((faq, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                        openFAQ === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`px-6 transition-all duration-300 ease-in-out ${
                      openFAQ === index
                        ? 'pb-4 max-h-[120rem] opacity-100'
                        : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                  >
                    <div className="text-gray-600 leading-relaxed py-3">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        confirmText="OK"
      />
    </AuthPageGuard>
  );
}
